import { useEffect, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/seo/Seo";
import { trackEvent } from "@/lib/analytics";
import { organizationSchema, professionalServiceSchema, websiteSchema, inspectorSchema, faqSchema, canonical } from "@/lib/seo";
import { HOMEPAGE_FAQS } from "@/components/fedari/Faq";
import Header from "@/components/fedari/Header";
import Hero from "@/components/fedari/Hero";
import Certification from "@/components/fedari/Certification";
import WhyChoose from "@/components/fedari/WhyChoose";
import WhoWeHelp from "@/components/fedari/WhoWeHelp";
import { ServicesGrid, ProcessSteps } from "@/components/fedari/Services";
import WhatWeInspect from "@/components/fedari/WhatWeInspect";
import ExampleFindings from "@/components/fedari/Story";
import InspectorGallery from "@/components/fedari/InspectorGallery";
import { InspectorBand, SampleReportBlock } from "@/components/fedari/Trust";
import Coverage from "@/components/fedari/Coverage";
import Communities from "@/components/fedari/Communities";
import { PriceGuide, FinalCta } from "@/components/fedari/Pricing";
import { PartnersBlock } from "@/components/fedari/Partners";
import Faq from "@/components/fedari/Faq";
import Footer from "@/components/fedari/Footer";

const QuoteDialog = lazy(() => import("@/components/booking/QuoteDialog"));

const homeFaqSchema = faqSchema(HOMEPAGE_FAQS.map(([q, a]) => ({ q, a })));

export default function Landing() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteLoaded, setQuoteLoaded] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    let lenis, raf, idle;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    // Defer smooth-scroll init (and its bundle) to idle time so it doesn't
    // compete with hydration during the critical load window.
    const start = async () => {
      const { default: Lenis } = await import("lenis");
      lenis = new Lenis({ duration: 1.1, smoothWheel: true });
      raf = requestAnimationFrame(loop);
    };
    idle = window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 2000 }) : setTimeout(start, 1200);
    return () => {
      if (window.cancelIdleCallback && typeof idle === "number") window.cancelIdleCallback(idle); else clearTimeout(idle);
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
    };
  }, []);

  const onQuote = () => { setQuoteLoaded(true); setQuoteOpen(true); };
  const onBook = onQuote; // legacy booking retired — all CTAs open the quote journey

  // Branded-search measurement: one event per homepage view, no PII.
  useEffect(() => { trackEvent("branded_homepage_view"); }, []);

  return (
    <div className="bg-white pb-16 lg:pb-0">
      <Seo
        title="Property Snagging and Inspection Dubai | Fedari"
        description="Independent snagging, handover and resale inspections in Dubai. Clear photographic findings and fixed pricing before booking. Get a quote from Fedari."
        path="/"
        jsonLd={[organizationSchema(), professionalServiceSchema(), inspectorSchema(), websiteSchema(), homeFaqSchema]}
      />
      <Header onBook={onBook} onQuote={onQuote} />
      <main id="main-content">
        <Hero onQuote={onQuote} />
        <Certification />
        {/* Factual one-paragraph summary — also what AI/search engines lift */}
        <section className="wrap py-10 border-b border-line" data-testid="factual-summary">
          <p className="text-[0.98rem] text-bodytext max-w-[78ch] mx-auto text-center">
            Fedari Inspection provides independent property snagging and inspections in Dubai for owners, buyers and investors. We inspect agreed accessible areas of <Link to="/apartment-inspection-dubai" className="text-blue font-medium hover:underline">apartments</Link>, <Link to="/villa-inspection-dubai" className="text-blue font-medium hover:underline">villas</Link>, townhouses and <Link to="/commercial-property-inspection-dubai" className="text-blue font-medium hover:underline">commercial properties</Link>, document visible findings and provide a photographic report with clear priorities. Fees, scope, reporting timeframe and any reinspection arrangements are confirmed in your quotation. <Link to="/sample-snagging-report" className="text-blue font-medium hover:underline">See a sample report</Link>, read <Link to="/about-fedari" className="text-blue font-medium hover:underline">about us</Link> or <Link to="/contact" className="text-blue font-medium hover:underline">contact the team</Link>.
          </p>
        </section>
        <div className="border-y border-line bg-white" data-testid="trust-strip">
          <div className="wrap py-3.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center font-mono-jb text-[0.7rem] tracking-wide uppercase text-muted">
            <span>Fedari Building Inspection L.L.C.</span>
            <span aria-hidden="true" className="opacity-40">·</span>
            <span>Trade Licence 1647070</span>
            <span aria-hidden="true" className="opacity-40">·</span>
            <span>Independent inspection and reporting</span>
            <span aria-hidden="true" className="opacity-40">·</span>
            <span>Fixed fee confirmed before booking</span>
          </div>
        </div>
        <WhyChoose />
        <WhoWeHelp />
        <ServicesGrid onQuote={onQuote} />
        <WhatWeInspect />
        <Coverage onQuote={onQuote} />
        <ExampleFindings />
        <ProcessSteps onQuote={onQuote} />
        <InspectorGallery />
        <InspectorBand />
        <SampleReportBlock />
        <PriceGuide onQuote={onQuote} />
        <Communities />
        <Faq />
        <PartnersBlock />
        <FinalCta onQuote={onQuote} />
      </main>
      <Footer onBook={onBook} onQuote={onQuote} />
      {quoteLoaded && <Suspense fallback={null}><QuoteDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} /></Suspense>}
    </div>
  );
}
